<template>
    <ModalContent stitch flat-chrome>
        <template slot="title">{{ title }}</template>
        <template slot="content">
            <div class="bank-account-grid">
                <v-select v-if="!params.bankOnly" v-model="form.kind" @change="focusBankName" :items="accountKinds" :label="$translate('accountKind')" hide-details="auto" :error-messages="fieldErrors.kind" outlined dense  :disabled="saving" />
                <v-text-field
                    v-if="form.kind === 'bank'"
                    ref="bankName"
                    autofocus
                    :value="form.bank_name"
                    @input="updateBankName"
                    :label="$translate('labels.bankName')"
                    :error-messages="fieldErrors.bank_name"
                    maxlength="140"
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="saving"
                />
                <v-text-field
                    class="bank-account-name"
                    v-model="form.label"
                    :label="$translate('labels.label')"
                    :error-messages="fieldErrors.label"
                    maxlength="80"
                    dense
                    outlined
                    hide-details="auto"
                    :autofocus="form.kind !== 'bank'"
                    :disabled="saving"
                />
                <v-text-field
                    v-if="['bank', 'provider'].includes(form.kind)"
                    v-model="form.account_holder"
                    @input="holderEdited = true"
                    :label="$translate('labels.accountHolder')"
                    :error-messages="fieldErrors.account_holder"
                    maxlength="140"
                    dense
                    outlined
                    hide-details="auto"
                    required
                    :disabled="saving"
                />
                <v-text-field
                    v-if="form.kind === 'bank'"
                    v-model="form.iban"
                    :label="$translate('labels.iban')"
                    :error-messages="fieldErrors.iban"
                    maxlength="42"
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="saving"
                />
                <v-text-field
                    v-if="form.kind === 'bank'"
                    v-model="form.bic"
                    :label="$translate('labels.bic')"
                    :error-messages="fieldErrors.bic"
                    maxlength="11"
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="saving"
                />
                <v-text-field
                    v-model="form.accounting_code"
                    :label="$translate('labels.accountingCode')"


                    :error-messages="fieldErrors.accounting_code"
                    maxlength="20"
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="saving"
                >
                    <template #label>
                        <span>{{ $translate('labels.accountingCode') }}</span>
                        <StitchHint :hint="$translate('accountingCodeHelp')" />
                    </template>
                </v-text-field>
            </div>
            <div v-if="form.kind === 'bank'" class="mt-5">
                <v-checkbox
                    v-model="form.is_default"
                    :label="$translate('labels.default')"
                    :disabled="defaultLocked"
                    :error-messages="fieldErrors.is_default"
                    dense
                    hide-details="auto"
                    class="mt-0"
                />
            </div>
        </template>
        <template slot="bottom">
            <Button
                class="my-2 my-sm-4 w-100 w-sm-auto"
                ghost
                :disabled="saving"
                :text="$translate('buttons.cancel')"
                @click="hideModal()"
            />
            <Button
                class="my-2 my-sm-4 w-100 w-sm-auto ml-sm-3"
                :disabled="saving"
                :loading="saving"
                :text="$translate('buttons.save')"
                @click="save"
            />
        </template>
    </ModalContent>
</template>

<script>
import axios from "@/plugins/axios";
import GlobalMixins from "@/mixins/global";

function emptyForm(isFirstAccount, accountHolder) {
    return {
        kind: "bank",
        label: "",
        account_holder: accountHolder || "",
        iban: "",
        bic: "",
        bank_name: "",
        accounting_code: "",
        is_default: !!isFirstAccount,
    };
}

function formatIban(value) {
    if (!value) {
        return "";
    }
    return String(value)
        .replace(/\s+/g, "")
        .toUpperCase()
        .replace(/(.{4})/g, "$1 ")
        .trim();
}

export default {
    mixins: [GlobalMixins],
    maxWidth: "720px",
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    data() {
        const account = this.params.bankAccount;
        const isFirstAccount = !!this.params.isFirstAccount;
        return {
            saving: false,
            holderEdited: false,
            fieldErrors: {},
            form: account
                ? {
                      ...account,
                      iban: formatIban(account.iban),
                  }
                : emptyForm(isFirstAccount, this.params.billingaccountName),
        };
    },
    created() { this.prefillHolder(); },
    computed: {
        accountKinds() { return ['bank', 'cash', 'provider', 'transit'].map(value => ({ value, text: this.$translate(`kinds.${value}`) })); },
        isNew() {
            return !this.form?.id;
        },
        title() {
            return this.isNew
                ? this.$translate(this.params.bankOnly ? "titles.add" : "titles.addTreasury")
                : this.$translate(this.params.bankOnly ? "titles.edit" : "titles.editTreasury");
        },
        defaultLocked() {
            if (this.isNew) {
                return !!this.params.isFirstAccount;
            }
            return !!this.params.bankAccount?.is_default;
        },
        billingAccountId() {
            return this.params.billingaccountId;
        },
    },
    methods: {
        focusBankName() {
            if (this.form.kind === 'bank') this.$nextTick(() => this.$refs.bankName?.focus());
        },
        async prefillHolder() {
            if (!this.isNew || this.form.account_holder || !this.billingAccountId) return;
            try {
                const { data } = await axios.get(`/api/billingaccounts/${this.billingAccountId}/`);
                if (!this.holderEdited && !this.form.account_holder) this.form.account_holder = data.name || '';
            } catch (_) {
                // The holder remains editable if the account details are unavailable.
            }
        },
        updateBankName(value) {
            if (!this.form.label || this.form.label === (this.form.bank_name || '').trim()) this.form.label = (value || '').trim();
            this.form.bank_name = value;
        },
        firstError(err) {
            if (!err) {
                return null;
            }
            return Array.isArray(err) ? err[0] : err;
        },
        async save() {
            if (this.saving || !this.billingAccountId) {
                return;
            }
            this.saving = true;
            this.fieldErrors = {};
            const payload = {
                kind: this.form.kind || "bank",
                label: this.form.label || "",
                account_holder: this.form.account_holder || "",
                iban: String(this.form.kind === "bank" ? this.form.iban || "" : "").replace(/\s+/g, "").toUpperCase(),
                bic: String(this.form.kind === "bank" ? this.form.bic || "" : "").replace(/\s+/g, "").toUpperCase(),
                bank_name: this.form.bank_name || "",
                accounting_code: this.form.accounting_code || "",
                is_default: this.form.kind === "bank" && !!this.form.is_default,
            };
            try {
                let response;
                if (this.form.id) {
                    response = await axios.patch(
                        `/api/billingaccounts/${this.billingAccountId}/bankaccounts/${this.form.id}/`,
                        payload
                    );
                } else {
                    response = await axios.post(
                        `/api/billingaccounts/${this.billingAccountId}/bankaccounts/`,
                        payload
                    );
                }
                this.addAlert(
                    this.isNew
                        ? this.$translate("messages.created")
                        : this.$translate("messages.updated")
                );
                this.hideModal({ result: response.data });
            } catch (error) {
                this.fieldErrors = error?.response?.data?.details || error?.response?.data || {};
                if (!Object.keys(this.fieldErrors).length) {
                    this.$swal({
                        title: this.$translate("messages.saveFailed"),
                        html: this.$utils.resultToSwalContent(this, error),
                        type: "error",
                    });
                }
            } finally {
                this.saving = false;
            }
        },
    },
    i18n: {
        messages: {
            en: {
                accountingCodeHelp: "Leave blank for automatic assignment when saving.",
                accountKind: "Account type", kinds: { bank: "Bank", cash: "Cash", provider: "Payment provider", transit: "Transit account" },
                kindHelp: { bank: 'Account receiving bank transfers and card settlements.', cash: 'Physical cash only. Card payments are received in a bank or payment provider account.', provider: 'Money held by a payment provider, such as Stripe or PayPal.', transit: 'Money awaiting transfer to another account.' },
                titles: { addTreasury: 'Add an account', editTreasury: 'Edit treasury account',
                    add: "Add a bank account",
                    edit: "Edit bank account",
                },
                labels: {
                    label: "Account name",
                    accountHolder: "Account holder",
                    iban: "IBAN (optional)",
                    bic: "BIC (optional)",
                    bankName: "Bank",
                    accountingCode: "Accounting code (optional)",
                    default: "Default",
                },
                buttons: {
                    cancel: "Cancel",
                    save: "Save",
                },
                messages: {
                    created: "Treasury account created",
                    updated: "Treasury account updated",
                    saveFailed: "The treasury account could not be saved",
                },
            },
            fr: {
                accountingCodeHelp: "Laissez vide pour une attribution automatique à l’enregistrement.",
                accountKind: "Type", kinds: { bank: "Banque", cash: "Caisse (espèces)", provider: "Prestataire de paiement", transit: "Compte de transit" },
                kindHelp: { bank: 'Compte qui reçoit les virements et les versements des paiements par carte.', cash: 'Espèces uniquement. Les paiements CB arrivent sur un compte bancaire ou chez un prestataire de paiement.', provider: 'Fonds détenus chez un prestataire de paiement, comme Stripe ou PayPal.', transit: 'Fonds en attente de transfert vers un autre compte.' },
                titles: { addTreasury: 'Ajouter un compte', editTreasury: 'Modifier le compte de trésorerie',
                    add: "Ajouter un compte bancaire",
                    edit: "Modifier le compte bancaire",
                },
                labels: {
                    label: "Nom du compte",
                    accountHolder: "Titulaire",
                    iban: "IBAN (facultatif)",
                    bic: "BIC (facultatif)",
                    bankName: "Banque",
                    accountingCode: "Code comptable (facultatif)",
                    default: "Par défaut",
                },
                buttons: {
                    cancel: "Annuler",
                    save: "Enregistrer",
                },
                messages: {
                    created: "Compte de trésorerie créé",
                    updated: "Compte de trésorerie mis à jour",
                    saveFailed: "Le compte de trésorerie n'a pas pu être enregistré",
                },
            },
        },
    },
};
</script>

<style scoped>
.bank-account-name { grid-column: 1; }
.bank-account-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px 16px;
}
@media (max-width: 600px) {
    .bank-account-grid {
        grid-template-columns: 1fr;
        gap: 16px;
    }
}
</style>
